import type { TFunction } from "i18next";

/**
 * Maps raw `lastError` strings from DGI automation (Python) and the API to i18n keys.
 * Unknown or technical strings fall back to a generic user-facing message.
 */
const EXACT_MESSAGE_KEYS: Record<string, string> = {
  "Document page still shows login required after retries; session may be incomplete":
    "dgiDocuments.modal.errors.documentLoginWall",
  "Cancelled by user": "dgiDocuments.modal.errors.cancelledByUser",
  "No identity provider selected": "dgiDocuments.modal.errors.noIdentityProvider",
  "Missing document number": "dgiDocuments.modal.errors.missingDocumentNumber",
  "Missing TuID document": "dgiDocuments.modal.errors.missingTuidDocument",
  "PDF capture failed or empty": "dgiDocuments.modal.errors.pdfCaptureFailed",
  "Success callback failed": "dgiDocuments.modal.errors.successCallbackFailed",
  "DGI servicios session not ready after SSO (login wall did not clear in time)":
    "dgiDocuments.modal.errors.dgiSessionNotReadyAfterSso",
  "DGI linked-entity selection failed": "dgiDocuments.modal.errors.dgiLinkedEntityFailed",
  "DGI Aviso al Contribuyente step failed": "dgiDocuments.modal.errors.dgiAvisoStepFailed",
  "DGI servicios session not ready after linked-entity or Aviso al Contribuyente step (login wall did not clear in time)":
    "dgiDocuments.modal.errors.dgiSessionNotReadyAfterEntity",
  "companyRut is required to choose a business on the DGI linked-entities page":
    "dgiDocuments.modal.errors.companyRutRequired",
  "Could not find a table row matching the company RUT on the DGI linked-entities page":
    "dgiDocuments.modal.errors.entityRowNotFound",
  "Continuar control not found on Aviso al Contribuyente (check iframe content)":
    "dgiDocuments.modal.errors.continuarNotFoundAviso",
  "Invalid PDF data": "dgiDocuments.modal.errors.invalidPdfData",
  "PDF too small": "dgiDocuments.modal.errors.pdfTooSmall",
  "Could not store PDF": "dgiDocuments.modal.errors.couldNotStorePdf",
  "Automation failed": "dgiDocuments.modal.errors.automationFailed",
  "Automation not available": "dgiDocuments.modal.errors.automationNotAvailable",
};

export function translateDgiConsultaLastError(raw: string | undefined, t: TFunction): string {
  const s = (raw ?? "").trim();
  if (!s) {
    return t("dgiDocuments.modal.failed");
  }

  // Own-property check, not a bare index: `s` is an arbitrary automation string,
  // and a plain object literal resolves "toString" / "constructor" / "__proto__"
  // off Object.prototype — handing t() a FUNCTION instead of an i18n key, i.e.
  // a crash inside the path that is already reporting a failure.
  const exactKey = Object.prototype.hasOwnProperty.call(EXACT_MESSAGE_KEYS, s)
    ? EXACT_MESSAGE_KEYS[s]
    : undefined;
  if (exactKey) {
    return t(exactKey);
  }

  if (s.startsWith("Could not click IDP")) {
    return t("dgiDocuments.modal.errors.idpClickFailed");
  }
  if (s.startsWith("TuID app flow click:")) {
    return t("dgiDocuments.modal.errors.tuidClickFailed");
  }
  if (s.startsWith("Unknown documentKey:")) {
    return t("dgiDocuments.modal.errors.unknownDocument");
  }
  if (s.startsWith("Could not click Continuar on Aviso al Contribuyente page:")) {
    return t("dgiDocuments.modal.errors.continuarClickFailed");
  }

  return t("dgiDocuments.modal.errors.unexpected");
}
